<template>
  <div class="flex-1 flex flex-col">
    <PropertyHeader
      :sub-sections="[entityLabel]"
    >
      <GMMenuButton v-if="currentStep && !hasSaved" align="right" is-nav
                    tertiary
                    icon-only
                    class="uppercase">
        <template #trigger="{ isVisible }">
            <span class="sr-only sm:not-sr-only sm:mr-2 text-link-text">
              {{ t(currentStep?.Title || '') }}
            </span>
          <GMIcon :name="isVisible ? 'chevron-up' : 'chevron-down'" class="size-6"/>
        </template>
        <GMMenuButtonOption
            v-for="{step, index} in indexedSteps"
            :key="`menuoption_${step.Page}`"
            :is-nav-active="step.Page === currentStep.Page"
            :disabled="index > stepsCompletedUpToIndex"
            @click="goToPage(step.Page)"
        >
          {{ t(step?.Title || '') }}
        </GMMenuButtonOption>
      </GMMenuButton>
    </PropertyHeader>

    <div v-if="hasSaved && !isPendingSaveContract"
         class="w-full flex flex-1 flex-col justify-center items-center mt-10">
      <GMIcon name="check" color="green" class="w-36"/>
      <p class="mt-2 mb-6">{{ savedText }}</p>
      <div class="w-full flex flex-col gap-8 justify-center items-center mt-6">
        <GMButton
            secondary
            class="uppercase"
            @click="navigateToFrontPage"
        >
          {{ t('GO_TO_FRONTPAGE') }}
        </GMButton>
        <div class="flex flex-col items-center justify-center flex-1 w-11/12 text-lg uppercase max-h-16">
          <div class="relative flex justify-center w-full border-t-2 border-border-2">
            <div class="-bottom-3.5 absolute px-6 bg-bg">
              {{ t('OR') }}
            </div>
          </div>
        </div>
        <GMButton
            secondary
            class="uppercase"
            @click="navigateToContract"
        >
          {{ t(sentQuoteTo ? 'SEE_QUOTE' : 'SEE_AGREEMENT') }}
        </GMButton>
      </div>
    </div>

    <div v-else-if="contract && !isPendingSaveContract"
         class="relative gm-container !max-w-4xl mx-auto mt-4 sm:mt-5 lg:mt-6">
      <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-6 sm:gap-4 mb-4 sm:mb-5 lg:mb-6">
        <GMHeading :depth="1" :size="'xl'" class="break-words">
          {{ t(currentStep?.Title) }}
        </GMHeading>
        <div id="wizard-step-actions" class="flex gap-2 empty:hidden"/>
      </div>
      <NuxtPage
          @hidenav="showNav = false"
          @shownav="showNav = true"
          @go-next="nextStep ? goToPage(nextStep.Page) : noop()"
          @go-back="previousStep ? goToPage(previousStep.Page) : noop()"
      />
    </div>

    <div v-else-if="isLoadingContractReference || isPendingSaveContract"
         class="flex flex-1 items-center justify-center w-full space-x-2">
      <div v-if="isLoadingContractReference" class="flex flex-1 items-center justify-center w-full space-x-2">
        <GMLoader size="small"/>
        <AnimatedText text="Laster kontrakt..."/>
      </div>
      <div v-else-if="isPendingSaveContract" class="flex-1 justify-center flex flex-col items-center mt-8">
        <GMLoader size="large" class="text-blue-500 mx-auto">
          <div class="flex items-end">
            <GMIcon
                name="document"
                class="w-10 h-10 text-blue-300 dark:text-blue-600"
            />
            <GMIcon
                name="arrow-right"
                class="-ml-5 w-8 h-8 text-blue-500 dark:text-blue-400"
            />
          </div>
        </GMLoader>
        <div class="text-center text-state-info-text">
          <AnimatedText
              v-if="sendingQuoteTo"
              class="text-lg"
              data-testid="sending-quote"
              :text="t('QUOTE.SENDING', sendingQuoteTo, {count: sendingQuoteTo})"
          />
          <AnimatedText
              v-else-if="contract?.StatusSbl === STATUS_SBL_DRAFT"
              class="text-lg"
              :text="t('SAVING_AND_SYNCHRONIZING_DRAFT')"
          />
          <AnimatedText v-else class="text-lg" :text="isSigned ? t('FINISHING_CONTRACT') : t('SAVING_CONTRACT')"/>
          <p class="text-sm opacity-70"> {{ t('PLEASE_WAIT') }} </p>
        </div>
      </div>
    </div>

    <ContentFooter
        v-if="!isPendingSaveContract && hasContractData && currentStep && showNav"
    >
      <div class="w-1/3 text-left">
        <GMButton
            v-if="previousStep"
            tertiary
            icon-before="chevron-left"
            class="uppercase tracking-wider"
            @click="goToPage(previousStep.Page)"
        >
              <span class="sr-only sm:not-sr-only">
              {{ t('PREV') }}
              </span>
        </GMButton>
      </div>

      <div class="inline-block w-fit mx-auto">
        <GMMenuButton v-if="currentStep && !hasSaved" direction="up" align="center" is-nav>
          <template #trigger="{ isVisible }">
           
            <span class="inline-block sm:hidden mr-2 dark:text-blue-300 ">
              {{ `${currentStepIndex + 1}/${steps.length}` }}
            </span>
              <span class="hidden sm:inline-block text-base uppercase whitespace-nowrap">{{
                  `${currentStepIndex + 1}/${steps.length} ${t(currentStep.Title)}`
                }}</span>
            <GMIcon
                :name="isVisible ? 'chevron-up' : 'chevron-down'" class="size-6"
            />
          </template>
          <GMMenuButtonOption
              v-for="{step, index} in [...indexedSteps].reverse()"
              :key="`menuoption_${step.Page}`"
              :is-nav-active="step.Page === currentStep.Page"
              :disabled="index > stepsCompletedUpToIndex"
              @click="goToPage(step.Page)"
          >
            {{ t(step?.Title || '') }}
          </GMMenuButtonOption>
        </GMMenuButton>
      </div>
      <div class="w-1/3 text-right">
        <GMButton
            v-if="nextStep"
            tertiary
            icon-after="chevron-right"
            class="uppercase tracking-wider"
            :disabled="!valid"
            @click="goToPage(nextStep.Page)"
        >
          <span class="sr-only sm:not-sr-only">{{ t('NEXT') }}</span>
        </GMButton>
      </div>
    </ContentFooter>
  </div>
</template>

<script lang="ts">
import {
  GMBadge,
  GMButton,
  GMIcon,
  GMLoader,
  GMMenuButton,
  GMMenuButtonOption,
  GMSelect,
  GMHeading,
  notify
} from '@gm/components'
import type {FullContract} from '~/models/contract'
import {useDraftNotice} from '~/helpers/saveDraftNotice'
import {useContractStore} from '~/stores/contract'
import {
  getContractStateLabel,
  getContractTypeLabel,
  hasUnsavedChanges,
  isDraft,
} from '~/helpers/contracts'
import {useHeaderStore} from '~/use/ui/header'
import {
  STATUS_SBL_SIGNED,
  STATUS_SBL_BANKID_REJECTED,
  STATUS_SBL_DRAFT, STATUS_SBL_AWAITING_SIGNATURE
} from '~/constants/contract'
import {isStatusSblAtLeast} from '~/helpers/statusSbl'
import {useOnline} from '@vueuse/core';
import {useContractService} from "~/services/contractService";
import {useMutationState} from "@tanstack/vue-query";
import {isTempContract} from "~/helpers/contractHelper";
import {useGetContractSaveMutationState, useIsSavingContract} from "~/composables/useIsSavingContract";
import {CONTRACT_SIGNATURE_TYPE_CODE_BANKID, ENTITY_TYPE_CONTRACT} from "~/constants/contracts";
import {useCustomerService} from "~/services/customerService";
import {useProperty} from "~/composables/useProperty";
import {queryClient} from "~/plugins/vue-query";
import ContentFooter from "~/components/ui/ContentFooter.vue";
import PropertyHeader from "~/components/Property/PropertyHeader.vue";
import {useWizardSectionSetup} from "~/composables/useWizardSectionSetup";
import {getEntityLabelConfig} from "~/helpers/entity";
import type {StepDefinition} from "~/models/wizardSchema";
import {useWizardStepValidity} from "~/composables/useWizardStepValidity";
import {getStepsForContract} from "~/helpers/wizardSteps";
import {cloneDeep} from "lodash";

export default defineComponent({
  name: 'EditContractPage',
  components: {
    PropertyHeader,
    GMHeading,
    ContentFooter,
    GMLoader,
    GMBadge,
    GMButton,
    GMMenuButton,
    GMMenuButtonOption,
    GMSelect,
    GMIcon,
  },
  setup() {
    const {t} = useTranslation()
    const route = useRoute()
    const router = useRouter()
    const customerId = route.params.customerId as string
    const propertyId = route.params.propertyId as string
    const {property: currentProperty} = useProperty(customerId, propertyId)
    const contractId = route.params.contractId as string
    const {customerQuery} = useCustomerService()
    const {data: currentCustomer} = customerQuery(customerId)
    const {contractQuery, saveContractMutation} = useContractService()
    const {mutate: saveContract} = saveContractMutation()
    const {data: contractReference, isLoading: isLoadingContractReference} = contractQuery(contractId)
    const isPendingSaveContract = useIsSavingContract(contractId)
    const hasSaved = ref(false)
    const contractStore = useContractStore()
    const hasContractData = computed(() => contractReference.value && JSON.stringify(contractReference.value) !== '{}')
    // Must check this before calling contractStore.getContractDraftById
    watch([hasContractData, isLoadingContractReference, isPendingSaveContract], () => {
      if (!isLoadingContractReference.value && !hasContractData.value) {
        notify(t('error.EMPTY_CURRENT_CONTRACT').toString(), {
          type: 'error'
        })
        router.push(`/customers/${customerId}/${propertyId}`)
      }

      if (!!contractReference.value && !isPendingSaveContract.value && !hasSaved.value && !contractStore.contractDraftExists(contractId)) {
        contractStore.addContractDraft(contractReference.value)
      }
    }, {immediate: true})

    const contract = contractStore.getContractDraftById(contractId, contractReference.value)
    useWizardSectionSetup(contract)

    const {setHeaderLink} = useHeaderStore()
    const isOnline = useOnline()

    const isSigned = computed(
        () =>
            contract.value?.SignatureTypeCode !==
            CONTRACT_SIGNATURE_TYPE_CODE_BANKID &&
            (contract.value?.SignatureOwner || false) &&
            contract.value?.SignatureOwner.length > 0 &&
            (contract.value?.SignatureBuyer || false) &&
            contract.value?.SignatureBuyer.length > 0
    )

    const title = computed(() => `${t(getContractTypeLabel(contract.value?.Type))} ${contract.value?.ContractNo || (t(getContractStateLabel(contract.value)) as string).toLowerCase()} | ${currentProperty.value?.Name} | ${currentCustomer.value?.Name}`)
    watch(() => title.value, () => {
      useHead({title: title.value})
    }, {immediate: true})


    const {
      isShowing,
      showDraftNotice,
      discardDraft,
      saveDraft
    } = useDraftNotice(contractId)

    const showNav: Ref<boolean> = ref(true)
    const entityLabel: ComputedRef<string> = computed(() => {
      const config = getEntityLabelConfig(contract.value?.EntityType, contract.value?.Type)
      return t(config?.NewLabel)
    })
      
    const steps: ComputedRef<StepDefinition[]> = computed(() => getStepsForContract(contract.value))

    const stepsAsOptions: ComputedRef<{
      label: string,
      value: string
    }[]> = computed(() => steps.value.map(step => ({label: t(step.Title), value: step.Page})))
    const currentPage: ComputedRef<string> = computed(() => route.params.step as string)
    const currentStepIndex: ComputedRef<number> = computed(() => steps.value.findIndex(x => x.Page === currentPage.value))
    const currentStep: ComputedRef<StepDefinition | undefined> = computed(() => steps.value[currentStepIndex.value])
    const previousStep: ComputedRef<StepDefinition | null> = computed(() => currentStepIndex.value > 0 ? steps.value[currentStepIndex.value - 1]! : null)
    const nextStep: ComputedRef<StepDefinition | null> = computed(() => currentStepIndex.value > -1 && currentStepIndex.value < steps.value.length - 1 ? steps.value[currentStepIndex.value + 1]! : null)
    const indexedSteps: ComputedRef<{step: StepDefinition, index: number}[]> = computed(() => steps.value.map((step, index) => ({step, index})))

    const {isStepValid: isEntityStepValid} = useWizardStepValidity(contract, currentCustomer)

    const stepsCompletedUpToIndex: ComputedRef<number> = computed(() => {
      const firstInvalidIndex = steps.value.findIndex((step) => !isEntityStepValid(step))
      return firstInvalidIndex === -1 ? steps.value.length - 1 : firstInvalidIndex
    })

    onBeforeMount(() => {
      setHeaderLink({
        Url: `/customers/${route.params.customerId}/${route.params.propertyId}/${contractId}`,
        Label: 'CONTRACT'
      })
    })

    const isLockedForWizard = (loaded: FullContract | undefined): boolean =>
      !!loaded && typeof loaded.Id !== 'undefined' && !isDraft(loaded) && !!loaded.StatusSbl
      && isStatusSblAtLeast(loaded.StatusSbl, STATUS_SBL_SIGNED)
    watch(() => contractReference.value, (loaded) => {
      if (isLockedForWizard(loaded)) {
        router.replace(`/customers/${customerId}/${propertyId}/${contractId}`)
      }
    }, { immediate: true })

    const noop = () => {}

    const goToPage = (page: string): void => {
      router.push(page)
    }
    const valid = computed(() => currentStep.value ? isEntityStepValid(currentStep.value) : false)

    onBeforeRouteLeave(async (to, _, next) => {
      // If redirect to login, assume user wants so save before being redirected back
      if (to.path.includes('/login')) {
        await saveDraft(contract.value)
        next()
        return
      }

      if (isOnline && !isShowing.value && hasContractData.value && contractStore.contractDraftExists(contractId) && hasUnsavedChanges(contract.value, contractReference.value)) {
        const result = await showDraftNotice(contract.value, contractReference.value as FullContract || {})

        switch (result) {
          case 'cancel': {
            next(false)
            break
          }
          case 'ignore': {
            next()
            contractStore.removeContractDraftById(contractId)
            break
          }
          case 'discard': {
            await discardDraft(contractId).then(() => {
              if (to.name === 'customers-customerId-propertyId-contractId') {
                next({
                  name: 'customers-customerId-propertyId',
                  params: {
                    customerId: currentCustomer.value.Id,
                    propertyId: propertyId,
                  }
                })
                return ''
              } else {
                next()
              }
            })
            contractStore.removeContractDraftById(contractId)
            if (isTempContract(contractId)) {
              queryClient.removeQueries({queryKey: ['contractGetContract', contractId]})
            }
            break
          }
          case 'saveDraft': {
            await saveDraft(contract.value)
            next()
            queryClient.setQueryData(['contractGetContract', contractId], () => contract.value)
            contractStore.removeContractDraftById(contractId)
            if (isTempContract(contractId)) {
              queryClient.removeQueries({queryKey: ['contractGetContract', contractId]})
            }
            break
          }
          case 'saveContract': {
            await discardDraft(contractId)
            await new Promise<boolean>((resolve) => {
              saveContract({contract: contract.value}, {
                onSuccess: (response) => {
                  resolve(true)
                },
                onError: () => {
                  resolve(false)
                },
                onSettled: () => {
                  contractStore.removeContractDraftById(contractId)
                  if (isTempContract(contractId)) {
                    queryClient.removeQueries({queryKey: ['contractGetContract', contractId]})
                  }
                }
              })
            })
            
            next()
            break
          }
          default: {
            next()
          }
        }
      } else {
        next()
        contractStore.removeContractDraftById(contractId)
      }
    })

    const newContractId = ref()
    const savedStatus = ref<number | undefined>()
    const sentQuoteTo = ref(0)
    const savedText = computed(() => {
      if (sentQuoteTo.value) {
        return t('QUOTE.SENT_TO_RECIPIENTS', sentQuoteTo.value, {count: sentQuoteTo.value})
      }
      switch (savedStatus.value) {
        case STATUS_SBL_AWAITING_SIGNATURE:
          return t('CONTRACT_SAVED_CONFIRMATIONS.AWAITING_SIGNATURE')
        case STATUS_SBL_SIGNED:
          return t('CONTRACT_SAVED_CONFIRMATIONS.SIGNED')
        default:
          return t('CONTRACT_SAVED_CONFIRMATIONS.DRAFT')
      }
    })

    const navigateToFrontPage = async () => {
      await router.push(`/`)
    }

    const navigateToContract = async () => {
      await router.push(`/customers/${customerId}/${propertyId}/${newContractId.value || contractId}/`)
    }

    type SavedContract = {Recipients?: unknown[] | null}
    const quoteRecipientCount = (saved?: SavedContract) => saved?.Recipients?.length ?? 0
    const pendingSaves = useGetContractSaveMutationState(contractId, 'pending')
    const sendingQuoteTo = computed(() =>
      quoteRecipientCount((pendingSaves.value.find(Boolean)?.state.variables as {contract?: SavedContract} | undefined)?.contract))

    const contractSaveMutationState = useGetContractSaveMutationState(contractId, 'success')
    type SaveMutation = NonNullable<(typeof contractSaveMutationState.value)[number]>
    const newestSuccessfulSave = (): SaveMutation | undefined =>
      contractSaveMutationState.value.reduce<SaveMutation | undefined>(
        (newest, mutation) => mutation && (!newest || mutation.mutationId > newest.mutationId) ? mutation : newest,
        undefined
      )

    // Successful saves from before the current save started must not count as its result
    let lastSeenSuccessfulSaveId = -1

    watch(() => isPendingSaveContract.value, (isPending) => {
      if (isPending) {
        lastSeenSuccessfulSaveId = newestSuccessfulSave()?.mutationId ?? -1
        showNav.value = false
        return
      }

      const succeeded = newestSuccessfulSave()
      if (succeeded && succeeded.mutationId > lastSeenSuccessfulSaveId) {
        const saved = (succeeded.state.data as {data?: {Id?: string}} | undefined)?.data
        const sent = (succeeded.state.variables as {contract?: SavedContract & {StatusSbl?: number}} | undefined)?.contract
        sentQuoteTo.value = quoteRecipientCount(sent)
        savedStatus.value = sent?.StatusSbl
        newContractId.value = saved?.Id
        hasSaved.value = true
      } else {
        // The save failed: stay in the wizard so the user can retry
        showNav.value = true
      }
    })

    return {
      STATUS_SBL_SIGNED,
      STATUS_SBL_BANKID_REJECTED,
      STATUS_SBL_DRAFT,
      t,
      route,
      currentProperty,
      contract,
      hasContractData,
      contractReference,
      isLoadingContractReference,
      currentCustomer,
      entityLabel,
      getContractTypeLabel,
      getContractStateLabel,
      steps,
      stepsAsOptions,
      indexedSteps,
      currentStep,
      currentStepIndex,
      previousStep,
      nextStep,
      showNav,
      stepsCompletedUpToIndex,
      valid,
      goToPage,
      noop,
      isOnline,
      isPendingSaveContract,
      isSigned,
      hasSaved,
      savedText,
      sendingQuoteTo,
      sentQuoteTo,
      navigateToFrontPage,
      navigateToContract,
    }
  },
  head: {
    title: ''
  }
})
</script>
